import { createFileRoute, Outlet, useNavigate, useParams } from '@tanstack/react-router'
import { Suspense, useMemo } from 'react'
import { Center, Loader } from '@mantine/core'
import { useSuspenseQuery } from '@tanstack/react-query'
import { getAllActiveRoutesOptions } from '@/api/main'
import { requirePermission } from '@/utils/auth'
import RoutePlanner from '@/components/RoutePlanner/RoutePlanner'
import {
  createModeAreaSearch,
  resolvePlannerBootstrap,
  returnRouteFromSearch,
} from '@/components/RoutePlanner/createModePolicy'
import { pickDefaultActiveRoute } from '@/components/RoutePlanner/defaultRoute'
import type { RouteId } from '@/components/RoutePlanner/types'

type RoutePlannerAreaSearch = {
  mode?: 'create'
  returnId?: string
  returnVersion?: string
}

export const Route = createFileRoute('/route-planner/$areaId')({
  beforeLoad: () => requirePermission('routes', 'write'),
  validateSearch: (raw: Record<string, unknown>): RoutePlannerAreaSearch => ({
    mode: raw.mode === 'create' ? 'create' : undefined,
    returnId: typeof raw.returnId === 'string' ? raw.returnId : undefined,
    returnVersion: typeof raw.returnVersion === 'string' ? raw.returnVersion : undefined,
  }),
  component: RoutePlannerAreaPage,
})

function RoutePlannerAreaPage() {
  const { areaId } = Route.useParams()
  const navigate = useNavigate()

  return (
    <Suspense
      fallback={
        <Center h="100vh">
          <Loader />
        </Center>
      }
    >
      <RoutePlannerAreaContent areaId={areaId} navigate={navigate} />
      <Outlet />
    </Suspense>
  )
}

function RoutePlannerAreaContent({ areaId, navigate }: { areaId: string; navigate: ReturnType<typeof useNavigate> }) {
  const search = Route.useSearch()
  const params = useParams({ strict: false })
  const deepLinkRoute = useMemo((): RouteId | undefined => {
    const routeId = typeof params.routeId === 'string' ? params.routeId : undefined
    const version = typeof params.version === 'string' ? params.version : undefined
    if (!routeId || !version) return undefined
    return { id: routeId, version }
  }, [params.routeId, params.version])

  const searchCreateMode = search.mode === 'create'
  const { data: activeRoutes } = useSuspenseQuery(getAllActiveRoutesOptions())
  const defaultRoute = useMemo(() => pickDefaultActiveRoute(activeRoutes, areaId), [activeRoutes, areaId])
  const bootstrap = useMemo(
    () =>
      resolvePlannerBootstrap({
        searchCreateMode,
        deepLinkRoute,
        defaultRoute,
      }),
    [searchCreateMode, deepLinkRoute, defaultRoute]
  )
  const returnRouteAfterCreate = useMemo(() => returnRouteFromSearch(search), [search])

  return (
    <RoutePlanner
      areaId={areaId}
      initialRoute={bootstrap.initialRoute}
      initialCreateMode={bootstrap.interactionMode === 'create'}
      returnRouteAfterCreate={returnRouteAfterCreate}
      onRouteSelectionChange={(route, meta) => {
        if (!route) {
          void navigate({
            to: '/route-planner/$areaId',
            params: { areaId },
            search: createModeAreaSearch(meta),
            replace: true,
            ignoreBlocker: meta?.ignoreBlocker === true,
          })
          return
        }
        void navigate({
          to: '/route-planner/$areaId/$routeId/$version',
          params: { areaId, routeId: route.id, version: route.version },
          search: {},
          replace: true,
          ignoreBlocker: meta?.ignoreBlocker === true,
        })
      }}
    />
  )
}
